import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode
} from 'react';
import { api, ApiClientError, setChildContext, setCsrfToken } from './api';
import type { AuthMe, Child } from './types';
import { flushOutbox } from './offline';

interface AppStatus {
  setupRequired: boolean;
  pushConfigured: boolean;
  mcpConfigured: boolean;
  githubMailboxConfigured: boolean;
  aiLocalFallback: boolean;
}

interface SetupInput {
  setupSecret: string;
  familyName: string;
  timezone: string;
  monthlyBudgetVnd: number;
  maxRewardPriceVnd: number;
  parentName: string;
  email: string;
  password: string;
  childNickname: string;
  birthYear: number | null;
}

interface AuthContextValue {
  loading: boolean;
  status: AppStatus | null;
  me: AuthMe | null;
  selectedChild: Child | null;
  selectedChildId: string;
  setSelectedChildId(id: string): void;
  refresh(): Promise<void>;
  login(email: string, password: string): Promise<void>;
  setup(input: SetupInput): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AppStatus | null>(null);
  const [me, setMe] = useState<AuthMe | null>(null);
  const [selectedChildId, setSelectedChildIdState] = useState(
    () => localStorage.getItem('kidee:selected-child') ?? ''
  );

  const applySelectedChild = useCallback((value: string) => {
    setSelectedChildIdState(value);
    setChildContext(value);
    if (value) localStorage.setItem('kidee:selected-child', value);
    else localStorage.removeItem('kidee:selected-child');
  }, []);

  const load = useCallback(async () => {
    const nextStatus = await api<AppStatus>('/status', { omitChildContext: true });
    setStatus(nextStatus);
    if (nextStatus.setupRequired) {
      setMe(null);
      setCsrfToken('');
      applySelectedChild('');
      return;
    }
    try {
      const nextMe = await api<AuthMe>('/auth/me', { omitChildContext: true });
      setCsrfToken(nextMe.csrfToken);
      setMe(nextMe);
      const stored = localStorage.getItem('kidee:selected-child') ?? selectedChildId;
      const currentExists = nextMe.children.some((child) => child.id === stored);
      const nextChildId = currentExists ? stored : (nextMe.children[0]?.id ?? '');
      applySelectedChild(nextChildId);
      void flushOutbox();
    } catch (error) {
      if (error instanceof ApiClientError && error.status === 401) {
        setMe(null);
        setCsrfToken('');
        applySelectedChild('');
      } else {
        throw error;
      }
    }
  }, [applySelectedChild, selectedChildId]);

  useEffect(() => {
    setChildContext(selectedChildId);
  }, [selectedChildId]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onOnline = () => void flushOutbox();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await api<{ csrfToken: string }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      omitChildContext: true
    });
    setCsrfToken(result.csrfToken);
    await load();
  }, [load]);

  const setup = useCallback(async (input: SetupInput) => {
    const { setupSecret, ...body } = input;
    const result = await api<{ csrfToken: string }>('/setup', {
      method: 'POST',
      setupSecret,
      body,
      omitChildContext: true
    });
    setCsrfToken(result.csrfToken);
    await load();
  }, [load]);

  const logout = useCallback(async () => {
    await api('/auth/logout', { method: 'POST', omitChildContext: true });
    setCsrfToken('');
    setMe(null);
    applySelectedChild('');
  }, [applySelectedChild]);

  const setSelectedChildId = useCallback((value: string) => {
    // The child list may have just changed after create/restore. Persist the requested
    // profile immediately; the next /auth/me refresh remains the source of truth and
    // automatically falls back if the id is not accessible.
    applySelectedChild(value);
  }, [applySelectedChild]);

  const selectedChild = me?.children.find((child) => child.id === selectedChildId) ?? me?.children[0] ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      loading,
      status,
      me,
      selectedChild,
      selectedChildId: selectedChild?.id ?? selectedChildId,
      setSelectedChildId,
      refresh,
      login,
      setup,
      logout
    }),
    [loading, status, me, selectedChild, selectedChildId, setSelectedChildId, refresh, login, setup, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
